/**
 * ai-bots-page.tsx — Nhân viên AI › "Nhân viên AI" (`/ai/nhan-vien`): danh sách nhân viên AI dạng thẻ.
 * Mỗi thẻ: ảnh, tên, nhãn "Mặc định", chip trạng thái ("Đang trả lời" / "Chưa trả lời: …" từ `status`), kênh phụ trách,
 * chế độ lúc này, model + nguồn. Bấm thẻ → màn nhân viên AI (`/ai/nhan-vien/:id`).
 * "Tạo nhân viên AI" → luồng tạo mới (`/ai/nhan-vien/moi`); "Key của tôi / Bot bên ngoài" → hộp Thêm nhân viên AI (Key của tôi / bot bên ngoài).
 * `openCreate` (route `/ai/nhan-vien/moi` tạm thời, tới khi có luồng tạo mới) mở hộp chọn cách tạo.
 */
import { useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Bot, PlugZap, Plus } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { PageHeader } from '@/components/shared/page-header'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { useAiCredits, useAiCreditsSocket } from '@/hooks/use-ai-credits'
import { useAiBots, useChannelOverrides, type AiBot } from '@/hooks/use-ai'
import { AI_PATHS, botPath, isDefaultBot } from '@/lib/ai-khung'
import { cn } from '@/lib/utils'
import { statusChip } from '@/components/ai/bot-status-lib'
import { scheduleSummary } from '@/components/ai/work-hours-lib'
import { avatarSrc } from '@/pages/settings/settings-utils'
import { AddAiMemberDialog } from '@/pages/team/add-ai-member-dialog'
import { KIND_BADGE, platformOf } from '@/pages/team/ai-member-form-lib'
import { AiCreditBanner, AiCreditChip } from './credits/credit-widgets'
import { BuyCreditsDialog } from './credits/buy-credits-dialog'
import { sortStaff, staffChannelsText, staffModeNow, staffModelText } from './staff/staff-list-lib'
import { useAiPerms } from './use-ai-perms'

const DEFAULT_EMOJI = '🤖'

export function AiBotsPage({ openCreate = false }: { openCreate?: boolean }) {
  const { t } = useTranslation('aiOps')
  const navigate = useNavigate()
  // Tạo / kết nối nhân viên AI = POST /ai/bots, /ai/bots/external-test → `ai.update`. Không có quyền → ẩn nút.
  const { canEdit } = useAiPerms()
  const [buyOpen, setBuyOpen] = useState(false)
  // Quyền tải không đồng bộ: giữ cờ mở theo `openCreate`, hộp chỉ render khi đã có quyền.
  const [connectOpen, setConnectOpen] = useState(openCreate)
  const { data: credits } = useAiCredits()
  useAiCreditsSocket()
  const { data, isLoading, isError } = useAiBots()
  const { data: channelData } = useChannelOverrides()
  const channelName = (id: string) => channelData?.channels.find((c) => c.id === id)?.displayName ?? null
  const bots = sortStaff(data?.bots ?? [])

  const create = () => navigate(AI_PATHS.newBot)

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('bots.title')}
        description={t('bots.description')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {credits && <AiCreditChip credits={credits} onBuy={() => setBuyOpen(true)} />}
            {canEdit && (
              <>
                <Button variant="outline" onClick={() => setConnectOpen(true)} title={t('bots.connectTitle')}>
                  <PlugZap className="h-4 w-4" /> {t('bots.connect')}
                </Button>
                <Button onClick={create}><Plus className="h-4 w-4" /> {t('bots.create')}</Button>
              </>
            )}
          </div>
        }
      />
      {credits && <AiCreditBanner credits={credits} onBuy={() => setBuyOpen(true)} />}
      <BuyCreditsDialog open={buyOpen} onOpenChange={setBuyOpen} />

      {isLoading ? (
        <Loading label={t('bots.loading')} />
      ) : isError || !data ? (
        <ErrorState message={t('bots.loadError')} />
      ) : bots.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <Bot className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">{t('bots.empty')}</p>
            {canEdit && (
              <>
                <p className="max-w-md text-xs text-muted-foreground">{t('bots.emptyHint')}</p>
                <Button className="mt-2" onClick={create}><Plus className="h-4 w-4" /> {t('bots.create')}</Button>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {bots.map((bot) => <StaffCard key={bot.id} bot={bot} channelName={channelName} />)}
        </div>
      )}

      {canEdit && <AddAiMemberDialog open={connectOpen} onOpenChange={setConnectOpen} canEdit={canEdit} />}
    </div>
  )
}

function StaffCard({ bot, channelName }: { bot: AiBot; channelName: (id: string) => string | null }) {
  const { t } = useTranslation('aiOps')
  const chip = statusChip(bot.status, bot.enabled)
  const channels = staffChannelsText(bot, channelName)
  const kindLabel = bot.kind === 'webhook' ? t('bots.webhookLabel') : platformOf(bot.kind)?.label ?? KIND_BADGE[bot.kind] ?? 'AI'
  const isDefault = isDefaultBot(bot)
  return (
    <Link to={botPath(bot.id)} className="block rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <Card className="h-full transition-shadow hover:shadow-md">
        <CardContent className="space-y-3 pt-5">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted text-2xl">
              {bot.avatarUrl ? <img src={avatarSrc(bot.avatarUrl)} alt="" className="h-full w-full object-cover" /> : bot.avatarEmoji || DEFAULT_EMOJI}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="truncate text-sm font-semibold">{bot.name}</span>
                {isDefault && <Badge variant="default" className="shrink-0">{t('bots.default')}</Badge>}
                {bot.kind !== 'builtin' && <Badge variant="secondary" className="shrink-0 font-normal">{KIND_BADGE[bot.kind]}</Badge>}
              </div>
              <Badge variant={chip.tone} className="mt-1 max-w-full truncate font-normal" title={chip.title}>{chip.text}</Badge>
            </div>
          </div>
          <dl className="space-y-1 border-t pt-3 text-xs">
            <Row label={t('bots.rowChannels')}>
              <span title={channels.names.join(', ') || undefined}>{channels.summary}</span>
              {channels.names.length > 0 && (
                <span className="mt-0.5 flex flex-wrap justify-end gap-1">
                  {channels.names.slice(0, 3).map((n, i) => <Badge key={`${n}-${i}`} variant="outline" className="max-w-36 truncate font-normal">{n}</Badge>)}
                  {channels.names.length > 3 && <span className="text-muted-foreground">+{channels.names.length - 3}</span>}
                </span>
              )}
            </Row>
            <Row label={t('bots.rowNow')}>
              <span>{staffModeNow(bot)}</span>
              <span className="block text-[11px] text-muted-foreground">{scheduleSummary({
                scheduleEnabled: !!bot.scheduleEnabled, scheduleStartHour: bot.scheduleStartHour, scheduleEndHour: bot.scheduleEndHour,
                scheduleDaytimeMode: bot.scheduleDaytimeMode, scheduleNighttimeMode: bot.scheduleNighttimeMode,
                defaultMode: bot.defaultMode ?? 'suggest',
              })}</span>
            </Row>
            <Row label={bot.kind === 'builtin' ? t('bots.rowModel') : t('bots.rowConnection')}>
              <span className={cn(bot.effective && !bot.effective.ready && 'text-destructive')} title={bot.effective?.reason ?? undefined}>
                {staffModelText(bot, kindLabel)}
              </span>
            </Row>
          </dl>
        </CardContent>
      </Card>
    </Link>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right font-medium">{children}</dd>
    </div>
  )
}
